import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { changeAutomaticTaxRateActiveStatus, createTaxRate, deleteTaxRate, getTaxRates, makeAutomaticTaxRateDefault, reorderTaxRates, setDefaultTaxRate, updateTaxRate, updateTaxRateActiveStatus } from "./taxRatesApi"
import { TaxRate } from "./taxRate"

export const useGetTaxRatesQuery = () => {
  return useQuery(['tax-rates'], {
    queryFn: getTaxRates,
    staleTime: 30 * 1000
  })
}

export const useCreateTaxRateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTaxRate,
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
    },
  })
}

export const useUpdateTaxRateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTaxRate,
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
    },
  })
}

export const useSetDefaultTaxRateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setDefaultTaxRate,
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem setting the default tax rate.' })
    },
  })
}

export const useMakeAutomaticTaxRateDefaultMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: makeAutomaticTaxRateDefault,
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem making Calculate Automatically the default.' })
    },
  })
}

export const useChangeAutomaticTaxRateActiveStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeAutomaticTaxRateActiveStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem updating the Calculate Automatically active status.' })
    },
  })
}

export const useReorderTaxRatesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderTaxRates,
    onMutate: async (data) => {
      const cached = queryClient.getQueryData(['tax-rates']) as TaxRate[] | undefined
      const optimisticUpdate = cached?.map(x => ({...x, sortIndex: data.find(y => y.taxRateId === x.id)?.sortIndex ?? x.sortIndex}))
      queryClient.setQueryData(['tax-rates'], optimisticUpdate)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
    }
  })
}

export const useDeleteTaxRateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTaxRate,
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
    }
  })
}

export const useUpdateTaxRateActiveStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTaxRateActiveStatus,
    onMutate: async (data) => {
      const cached = queryClient.getQueryData(['tax-rates']) as TaxRate[] | undefined
      const optimisticUpdate = cached?.map(x => ({...x, isActive: x.id === data.taxRateId ? data.isActive : x.isActive}))
      queryClient.setQueryData(['tax-rates'], optimisticUpdate)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tax-rates'])
    }
  })
}
